import { StyledGradientSpinner } from 'modules/ai/copyAssist/components/dropdown/styles';
import { StyledCopyAssistDivider } from 'modules/ai/copyAssist/styles';
import {
  useCopyAssistContext,
  useTakeoverExperimentContext,
  useVariantConfigurationContext,
} from 'modules/experiments/contexts';
import { useVariantTemplateValidationContext } from 'modules/experiments/contexts/VariantTemplateValidationContext';
import { getTakeoverExperimentId } from 'modules/experiments/ducks/selectors';
import {
  useAddExperimentVariant,
  useDeleteExperimentVariant,
  useExperimentTakeoverVariantList,
  useExperimentsTranslations,
  useSuggestionsAsVariants,
} from 'modules/experiments/hooks';
import { updateExperimentEditVariants } from 'modules/experiments/takeover/ducks/actions';
import { getTakeoverExperimentLocale } from 'modules/experiments/takeover/ducks/selectors';
import { CreateVariantsFromSuggestionsProps } from 'modules/experiments/types';
import { mutateEditExperimentVariantState } from 'modules/experiments/utils';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from 'ui/theme';
import { ExpyIcon } from '../icons/ExpyIcon';
import {
  CopyAssistMenuItemDisabled,
  CopyAssistMenuItemDisabledTooltip,
  CopyAssistMenuItemEnabled,
  CopyAssistMenuWrapper,
} from './Styled';

export type CopyAssistSuggestionsAsVariantsProps = {
  isLoading: boolean;
  isError?: boolean;
  suggestions: string[];
};

type CopyAssistMenuProps = {
  suggestions: string[];
};

type CopyAssistMenuItemProps = {
  suggestions: string[];
  isItemDisabled: boolean;
  isVariantsCreating: boolean;
  createVariantsFromSuggestions: (props: CreateVariantsFromSuggestionsProps) => Promise<void>;
};

type CopyAssistMenuItemTextProps = {
  isItemDisabled: boolean;
  isLoading: boolean;
};

const CopyAssistMenuItemText = ({ isLoading, isItemDisabled }: CopyAssistMenuItemTextProps) => {
  const { t } = useExperimentsTranslations();

  return isLoading ? (
    <>
      <StyledGradientSpinner width={16} height={16} />
      <span>{t('button.addSuggestionsAsVariantsLoading')}</span>
    </>
  ) : (
    <>
      <ExpyIcon color={!isItemDisabled ? colors.purple90 : colors.textDisabled} />
      <span>{t('button.addSuggestionsAsVariants')}</span>
    </>
  );
};

const CopyAssistMenuItem: React.FC<React.PropsWithChildren<CopyAssistMenuItemProps>> = ({
  suggestions,
  isItemDisabled,
  isVariantsCreating,
  createVariantsFromSuggestions,
  children,
}) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const experimentId = useSelector(getTakeoverExperimentId);
  const currentLocale = useSelector(getTakeoverExperimentLocale);
  const {
    overlay: { setIsOverlayEnable },
  } = useTakeoverExperimentContext();
  const { selectedVariant, variantList, setPreviewSelectMode, updateExperimentVariant } =
    useVariantConfigurationContext();
  const reFetchVariantList = useExperimentTakeoverVariantList(experimentId, currentLocale);
  const { updateTemplateErrorsForAllVariantLocales } = useVariantTemplateValidationContext();
  const createExperimentVariant = useAddExperimentVariant({
    experimentId,
    showErrorToaster: false,
  });
  const deleteExperimentVariant = useDeleteExperimentVariant({
    experimentId,
    showErrorToaster: false,
    showSuccessToaster: false,
  });
  const { activatedFormField, closeCopyAssist } = useCopyAssistContext();

  /**
   * When we refetch the variant list after the new variants
   * are created and modified by the useSuggestionsAsVariants hook
   * we want to set the last variant as the selected variant before we
   * refresh the redux store.
   */
  const refetch = async () => {
    const reFetchedOriginalVariantList = await reFetchVariantList();
    const mutatedExperimentVariants = mutateEditExperimentVariantState(
      reFetchedOriginalVariantList,
    );

    setPreviewSelectMode(mutatedExperimentVariants[mutatedExperimentVariants.length - 1]);
    dispatch(updateExperimentEditVariants(reFetchedOriginalVariantList));
  };

  return isItemDisabled ? (
    <CopyAssistMenuItemDisabledTooltip
      tooltipContent={t('variantCard.tooltip.disableAddSuggestionsAsVariants')}
    >
      <CopyAssistMenuItemDisabled data-test="add-suggestions-as-variants-menu-item-disabled">
        {children}
      </CopyAssistMenuItemDisabled>
    </CopyAssistMenuItemDisabledTooltip>
  ) : (
    <CopyAssistMenuItemEnabled
      data-test="add-suggestions-as-variants-menu-item"
      isLoading={isVariantsCreating}
      onClick={async () => {
        // The activated form field needs to
        // be set for the CA dropdown to open.
        // So, this is more of a safety check.
        if (!activatedFormField) return;

        // Enable the overlay on the page
        // when we are creating the variants
        setIsOverlayEnable(true);

        await createVariantsFromSuggestions({
          experimentId,
          currentVariantCount: variantList.length,
          currentVariantId: selectedVariant?.id ?? 0,
          fieldName: activatedFormField,
          suggestions,
          currentLocale,
          updateExperimentVariant,
          createExperimentVariant,
          closeCopyAssistDropdown: closeCopyAssist,
          deleteExperimentVariant,
          refetch,
          validateTemplate: updateTemplateErrorsForAllVariantLocales,
        });

        setIsOverlayEnable(false);
      }}
    >
      {children}
    </CopyAssistMenuItemEnabled>
  );
};

const CopyAssistMenu = ({ suggestions }: CopyAssistMenuProps) => {
  const { variantList } = useVariantConfigurationContext();
  const { isVariantsCreating, createVariantsFromSuggestions } = useSuggestionsAsVariants();
  // Excluding the control variant and we would be adding n - 1 new variants for n suggestions
  const isItemDisabled = variantList.length - 1 + (suggestions.length - 1) > 9;

  return (
    <CopyAssistMenuWrapper>
      <CopyAssistMenuItem
        suggestions={suggestions}
        createVariantsFromSuggestions={createVariantsFromSuggestions}
        isVariantsCreating={isVariantsCreating}
        isItemDisabled={isItemDisabled}
      >
        <CopyAssistMenuItemText isLoading={isVariantsCreating} isItemDisabled={isItemDisabled} />
      </CopyAssistMenuItem>
    </CopyAssistMenuWrapper>
  );
};

export const CopyAssistSuggestionsAsVariants = ({
  isLoading,
  isError = false,
  suggestions,
}: CopyAssistSuggestionsAsVariantsProps) => {
  if (isLoading || isError) return null;

  return (
    <>
      <StyledCopyAssistDivider />
      <CopyAssistMenu suggestions={suggestions} />
    </>
  );
};
